import { useState, useEffect } from "react";
import { toast } from "sonner";
import { FiBell, FiLoader, FiMail, FiCheck } from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useUser } from "@/features/auth";

interface EmailPreferences {
  newsletter: boolean;
  rateAlerts: boolean;
  marketUpdates: boolean;
  promotions: boolean;
}

export function PreferencesPage() {
  const user = useUser();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [preferences, setPreferences] = useState<EmailPreferences>({
    newsletter: true,
    rateAlerts: true,
    marketUpdates: true,
    promotions: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch current subscription status and preferences
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await fetch("/api/customers/subscription", {
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json() as {
            isSubscribed: boolean;
            preferences: EmailPreferences | null;
          };
          setIsSubscribed(data.isSubscribed);
          if (data.preferences) {
            setPreferences(data.preferences);
          }
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  const handleToggle = (key: keyof EmailPreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const response = await fetch("/api/customers/subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ preferences }),
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        throw new Error(data.error || "Failed to save preferences");
      }

      setIsSubscribed(true);
      toast.success(isSubscribed ? "Preferences saved" : "Successfully subscribed!");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save preferences"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const preferenceItems = [
    {
      key: "newsletter" as const,
      title: "Monthly Newsletter",
      description:
        "Receive our monthly newsletter with market insights and tips",
    },
    {
      key: "rateAlerts" as const,
      title: "Rate Change Alerts",
      description:
        "Get notified when interest rates change or new products are available",
    },
    {
      key: "marketUpdates" as const,
      title: "Market Updates",
      description: "Receive updates about the property market and RBA news",
    },
    {
      key: "promotions" as const,
      title: "Special Offers",
      description: "Be the first to know about special offers and promotions",
    },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl font-medium text-primary">
          Preferences
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your notification settings
        </p>
      </div>

      {/* Email preferences */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FiBell className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Email Notifications</CardTitle>
          </div>
          <CardDescription>
            Choose which emails you'd like to receive from Path Financial
            Solutions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <FiLoader className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {preferenceItems.map((item) => (
                <div
                  key={item.key}
                  className="flex items-start gap-4 p-4 rounded-lg bg-muted/50"
                >
                  <div className="flex-1">
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={preferences[item.key]}
                    onClick={() => handleToggle(item.key)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      preferences[item.key] ? "bg-accent" : "bg-input"
                    }`}
                  >
                    <span
                      className={`pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                        preferences[item.key] ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              ))}

              <Button
                onClick={handleSave}
                className="btn-pill mt-4"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <FiLoader className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save Preferences"
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Subscription status */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FiMail className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Email Subscription</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {isSubscribed ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-600">
                <FiCheck className="w-4 h-4" />
                <span className="font-medium">Subscribed</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Your email ({user?.email}) is subscribed to receive communications
                from Path Financial Solutions. Manage your preferences above or
                unsubscribe by turning off all options.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Your email ({user?.email}) is not currently subscribed to our
                mailing list. Subscribe to receive market updates, rate alerts,
                and our monthly newsletter.
              </p>
              <Button onClick={handleSave} className="btn-pill" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <FiLoader className="animate-spin mr-2" />
                    Subscribing...
                  </>
                ) : (
                  "Subscribe Now"
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
